/**
 * Step 1 — "Lesson Time".
 *
 * Date, then time, then bay. The slot grid and the bay cards are the standard
 * flow's own `TimeSlotSelector` / `BaySelector`; what differs is the source,
 * which is the instructor's availability rather than the club's
 * (`lessonAvailabilitySource.ts`).
 *
 * @module pages/lessons/steps/LessonTimeStep
 */

import { useMemo } from 'react';
import { parseISO } from 'date-fns';
import { ArrowIcon } from '@butter/ui/icons';
import { DatePicker } from '@butter/ui/components/inputs';
import { BaySelector } from '@butter/ui/domains/booking';
import {
  asUtcDate,
  createClubDateTimeFromSlot,
  extractLocalDateISO,
  formatClubDateISO,
  minutesToTimeString,
  timeStringToMinutes,
} from '@butter/library/datetime';
import { DEFAULT_LESSON_DURATION_MINUTES, MAX_LESSON_DURATION_MINUTES } from '@butter/library/availability';
import { useAuth } from '~/services/auth';
import { isOverLessonDurationLimit } from '@butter/library/lessons';
import { useLessonFlow, useLessonBookingQuote } from '~/services/internalApi/lessons';
import { useClubQuery } from '~/services/internalApi/club/hooks/useClubQuery';
import { clubTodayAsPickerDate, clubMaxBookableDate } from '~/pages/booking/steps/clubCalendarBounds';
import { TimeSlotSelector, type Bay, type TimeSlot } from '~/pages/booking/components/TimeSlotSelector';
import { useLessonSlots, useLessonBays } from '../lessonAvailabilitySource';
import { LessonAvailabilityPending } from '../components';
import { LessonTicketCard } from './components';
import { useLessonInstructor } from '../useLessonInstructor';
import { resolveLessonBayName } from '../lessonBayName';

const SECONDARY_LINK = 'text-sm font-semibold text-theme-600 underline text-interactive:text-theme-700';

// react-state.md: stable references for props that sit in render/deps.
/** @unit minutes */
const LESSON_MID_DURATION_MINUTES = 90;
const LESSON_DURATION_OPTIONS: number[] = [DEFAULT_LESSON_DURATION_MINUTES, LESSON_MID_DURATION_MINUTES, MAX_LESSON_DURATION_MINUTES];
const NO_FILTER_BAYS: Bay[] = [];
const noop = () => {};

export function LessonTimeStep() {
  const { data: club } = useClubQuery();
  const {
    instructorId,
    instructorName,
    bayId,
    date,
    startTime,
    endTime,
    durationMinutes,
    lessonLength,
    setLessonLength,
    setDate,
    setTimeRange,
    selectBay,
    goToStep,
  } = useLessonFlow();

  // Same resolution as the payment step, so a deep link still names the instructor.
  const { isAuthenticated } = useAuth();
  const { instructor } = useLessonInstructor({
    instructorId,
    clubId: club?.id,
    isAuthenticated,
    guestInstructorsFallback: true,
    sessionName: instructorName,
  });
  const headingInstructorName = instructor?.name ?? null;

  // The day being shown. With no date chosen yet the picker shows the club's
  // today, so the grid has to ask about that same day.
  const shownDate = date ?? (club?.timezone ? formatClubDateISO(asUtcDate(new Date()), club.timezone) : null);
  const lessonSlots = useLessonSlots({ clubId: club?.id, instructorId, date: shownDate, durationMinutes: lessonLength });
  const lessonBays = useLessonBays({
    clubId: club?.id,
    instructorId,
    date: shownDate,
    startTime,
    endTime,
    clubBays: club?.bays,
  });
  // The whole day is empty for one stated reason — the instructor is off, the
  // club is closed. Said in the server's words rather than as a bare empty grid.
  const dayIsEmpty = !lessonSlots.isLoading && !lessonSlots.error && lessonSlots.emptyReason !== null;

  // Never a browser-local `Date` — the picker's own selectedDate defaults to
  // the club's current calendar day when no date is selected yet.
  const selectedDate = date
    ? parseISO(date)
    : club?.timezone
      ? parseISO(formatClubDateISO(asUtcDate(new Date()), club.timezone))
      : new Date();

  const handleDateChange = (pickerDate: Date) => {
    setDate(extractLocalDateISO(pickerDate));
  };

  // Mirrors useAvailabilitySelection.handleSlotsSelect (booking/shared) — no
  // string comparison of slot times, minutesToTimeString owns the "24:00"
  // end-of-day sentinel (slot-times.md).
  const handleSlotsSelect = (selected: TimeSlot[]) => {
    if (selected.length === 0) {
      setTimeRange(null);
      return;
    }

    const firstSlot = selected[0];
    const lastSlot = selected[selected.length - 1];
    // Picking a time on the day the picker opened on commits that day too:
    // the flow's date is otherwise still unset, and a time belongs to a date.
    setTimeRange(
      {
        startTime: minutesToTimeString(firstSlot.startMinutes),
        endTime: minutesToTimeString(lastSlot.startMinutes + (lastSlot.durationMinutes ?? 30)),
      },
      shownDate ?? undefined,
    );
  };

  const handleBaySelect = (id: number) => {
    const bay = lessonBays.bays.find((b) => b.id === id);
    if (bay) selectBay({ id, name: bay.name });
  };

  const selectedRange =
    startTime !== null && endTime !== null
      ? { startMinutes: timeStringToMinutes(startTime), endMinutes: timeStringToMinutes(endTime) }
      : null;
  const selectedSlots = useMemo(() => {
    if (startTime === null || endTime === null) return [];
    const from = timeStringToMinutes(startTime);
    const to = timeStringToMinutes(endTime);
    return lessonSlots.slots.filter((s) => s.startMinutes >= from && s.startMinutes < to).map((s) => s.startMinutes);
  }, [lessonSlots.slots, startTime, endTime]);

  const hasRange = startTime !== null && endTime !== null;
  const canContinue = instructorId !== null && bayId !== null && date !== null && hasRange;
  // club.bays only — no session-storage fallback.
  const resolvedBayName = bayId === null ? null : resolveLessonBayName(club?.bays, bayId);
  const showTicket = date !== null && startTime !== null && endTime !== null && bayId !== null;

  // The payment step's own quote (same key, so step 3 reads it from cache). A
  // slot end may be the "24:00" sentinel — createClubDateTimeFromSlot, never
  // createClubDateTime. The quote endpoint is member-only.
  const canResolveSlot = showTicket && !!club?.timezone;
  const quoteQuery = useLessonBookingQuote({
    clubId: club?.id,
    instructorId,
    bayId,
    startTime: canResolveSlot ? createClubDateTimeFromSlot(date, startTime, club.timezone).date.toISOString() : null,
    endTime: canResolveSlot ? createClubDateTimeFromSlot(date, endTime, club.timezone).date.toISOString() : null,
    enabled: isAuthenticated && canResolveSlot && !isOverLessonDurationLimit(durationMinutes ?? 0),
  });
  const quote = quoteQuery.quote;
  // Shown only when a card is owed; a covered lesson has no `totalCost`. `baseAmount` is
  // already net of any discount.
  const lessonPrice = quote?.isValid === true && quote.totalCost !== undefined ? quote.totalCost.baseAmount : null;

  return (
    <div data-testid="lesson-time-step" className={showTicket ? 'pb-24 md:pb-0' : undefined}>
      <button
        type="button"
        onClick={() => goToStep(0)}
        className="inline-flex items-center gap-2 text-lg font-semibold text-fg-quaternary text-interactive:text-fg-tertiary"
        data-testid="lesson-back-to-instructor-btn"
      >
        <ArrowIcon className="h-4 w-4 rotate-180" />
        Back to Instructor
      </button>

      <h2
        className="mt-8 mb-5 md:mb-6 text-xl md:text-2xl font-semibold text-fg-secondary"
        data-testid="lesson-header-day-select"
      >
        Select an available date and time
        {headingInstructorName && (
          <>
            {' '}
            with <span className="text-theme-600">{headingInstructorName}</span>
          </>
        )}
      </h2>

      <DatePicker
        floatingCalendar
        selectedDate={selectedDate}
        onDateSelect={handleDateChange}
        minDate={clubTodayAsPickerDate(club?.timezone)}
        // The server's own last bookable day once it has answered; the club's
        // advance window until then. Both are the rule the booking enforces.
        maxDate={
          lessonSlots.lastBookableDate
            ? parseISO(lessonSlots.lastBookableDate)
            : clubMaxBookableDate(club?.timezone, club?.advanceBookingDays ?? 14)
        }
      />

      {dayIsEmpty ? (
        <>
          <h3 className="mt-7 md:mt-8 text-lg md:text-xl font-semibold text-fg-secondary">Available time slots</h3>
          <div className="mt-4">
            <LessonAvailabilityPending
              title="No lesson times on this day"
              description={lessonSlots.emptyReason ?? 'This instructor has no lesson times on this day.'}
              reason="lesson-day-empty"
              action={
                <button type="button" onClick={() => goToStep(0)} className={SECONDARY_LINK} data-testid="lesson-pending-back-to-instructors">
                  Back to instructors
                </button>
              }
            />
          </div>
        </>
      ) : (
        <>
          <TimeSlotSelector
            graceMinutes={club?.bookingGraceMinutes}
            durationDisplay="select"
            showBayFilter={false}
            slots={lessonSlots.slots}
            bays={NO_FILTER_BAYS}
            durationOptions={LESSON_DURATION_OPTIONS}
            selectedBayId="all"
            selectedDuration={lessonLength}
            selectedSlots={selectedSlots}
            selectedRange={selectedRange}
            onBayChange={noop}
            onDurationChange={setLessonLength}
            onSlotsSelect={handleSlotsSelect}
            isLoading={lessonSlots.isLoading}
            error={lessonSlots.error}
            formatOptionLabel={(m) => `${m} min`}
            formatTriggerLabel={(m) => `${m} minutes`}
          />
          {lessonSlots.error && (
            <button type="button" onClick={() => lessonSlots.retry()} className={`mt-4 ${SECONDARY_LINK}`} data-testid="lesson-time-retry">
              Try again
            </button>
          )}
        </>
      )}

      {/* Bays are offered for a chosen time, never before one. */}
      {hasRange && (
        <section aria-label="Select a bay" className="mt-12">
          <BaySelector
            title="Select a bay"
            bays={lessonBays.bays}
            selectedBayId={bayId}
            onBaySelect={handleBaySelect}
            isLoading={lessonBays.isLoading}
            error={lessonBays.error}
            clubMessage="No bay is free for this instructor at that time. Please pick another time."
          />
          {lessonBays.error && (
            <button type="button" onClick={() => lessonBays.retry()} className={`mt-4 ${SECONDARY_LINK}`} data-testid="lesson-bays-retry">
              Try again
            </button>
          )}
        </section>
      )}

      {showTicket && resolvedBayName !== null && (
        <LessonTicketCard
          className="mt-10"
          instructorName={headingInstructorName ?? 'Your instructor'}
          bayName={resolvedBayName}
          onContinue={() => goToStep(2)}
          canContinue={canContinue}
          priceSlot={
            lessonPrice !== null ? (
              <div data-testid="lesson-ticket-price" className="text-left">
                <p className="text-28 leading-7 font-bold text-fg-primary">${lessonPrice.toFixed(2)}</p>
                <p className="text-base leading-5 font-medium text-butter-gray-500">Taxes not included</p>
              </div>
            ) : undefined
          }
        />
      )}
    </div>
  );
}
